import type { ButtonProps } from '@components/button/button'
import { Button } from '@components/button/button'
import type { ButtonTrackingProps, Preset } from '@components/button/props'
import { CountryCode, TelephoneInput } from '@components/input/telephone-input'
import { Tooltip } from '@components/tooltip/tooltip'
import type { ActionDownloadAppFragment } from '@graphql/fragments/ActionDownloadAppFragment.graphql'
import { CustomFieldId, useField } from '@graphql/hooks/field'
import { ErrorMessage } from '@hookform/error-message'
import { useStores } from '@lib/store/useStores'
import { waitlistStore } from '@lib/store/waitlist/WaitlistStore'
import type { WithTestId } from '@lib/utils/testid'
import { TEXT } from '@lib/utils/text'
import { animated } from '@react-spring/web'
import { telephoneToE164, validateTelephoneInput } from '@shared/util/phone'
import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import Link from 'next/link'
import { createRef, useEffect, useRef, useState } from 'react'
import type { FunctionComponent } from 'react'
import { useController, useForm } from 'react-hook-form'

import styles from './download-app.module.scss'
import { useButtonRolloutAnimation } from './hooks'

export const DownloadApp: FunctionComponent<React.PropsWithChildren<Props>> =
    observer(
        ({
            preset,
            title,
            size = 'medium',
            buttonProps,
            placement,
            containerElementId,
            loading,
            forceExpanded = false,
            displayOnLight = false,
            'data-testid': testId,
            onSubmitCallback,
            alignment = 'center',
            phoneNumber,
            autoSend = false,
            pagePath,
            collapseOnScroll,
        }) => {
            const [expanded, setExpanded] = useState(false)
            const [isFocused, setIsFocused] = useState(false)
            const [autoSendComplete, setAutoSendComplete] = useState(false)
            const [autoSendError, setAutoSendError] = useState(false)
            const autoSendAttempted = useRef(false)
            const { errorTexts, placeholder } = useField(
                CustomFieldId.AppLinkPhone,
            )

            const isExpanded = forceExpanded || expanded

            // conditions in which we show the help text
            const showHelpText = placement !== 'header' && size !== 'small'

            const { linkingStore } = useStores()

            const {
                handleSubmit,
                reset,
                setError,
                control,
                formState: {
                    errors,
                    isDirty,
                    isSubmitSuccessful,
                    submitCount,
                    isSubmitting,
                    isSubmitted,
                },
            } = useForm<{ phone: string }>({
                mode: 'onSubmit',
            })

            const buttonRef = createRef<HTMLButtonElement>()

            const { inputStyles, buttonStyles, formStyles } =
                useButtonRolloutAnimation(
                    isExpanded ? 'focused' : 'idle',
                    size,
                    placement,
                )

            const onSubmit = handleSubmit(async (values) => {
                const phone = await telephoneToE164(values.phone)

                if (!phone) {
                    setError('phone', { message: errorTexts.required })
                    return
                }

                try {
                    await linkingStore.sendDownloadLinkInSMS(phone, {
                        pagePath,
                    })
                    onSubmitCallback?.()
                } catch (e) {
                    setError('phone', { message: errorTexts.adblocker })
                    return
                }

                // clear success status after 2 seconds, to allow resending
                setTimeout(() => {
                    reset()
                }, 2000)
            })

            const onExpand = () => {
                setTimeout(() => setExpanded(true), 100)
            }

            const shouldCollapseOnScroll =
                collapseOnScroll ?? (placement === 'header' || size === 'small')

            useEffect(() => {
                if (!shouldCollapseOnScroll || !isExpanded) return

                const onCollapse = () => {
                    reset()
                    setExpanded(false)
                }
                window.addEventListener('scroll', onCollapse)
                return () => window.removeEventListener('scroll', onCollapse)
            }, [shouldCollapseOnScroll, isExpanded, reset])

            // Auto-send SMS on mount when phoneNumber is provided and autoSend is true
            useEffect(() => {
                if (
                    !autoSend ||
                    !phoneNumber ||
                    autoSendAttempted.current ||
                    autoSendComplete
                ) {
                    return
                }

                autoSendAttempted.current = true

                const sendSms = async () => {
                    try {
                        await linkingStore.sendDownloadLinkInSMS(phoneNumber, {
                            pagePath,
                        })
                        setAutoSendComplete(true)
                        onSubmitCallback?.()
                    } catch (error) {
                        console.error('Failed to auto-send SMS:', error)
                        setAutoSendError(true)
                    }
                }

                sendSms()
            }, [
                autoSend,
                phoneNumber,
                linkingStore,
                onSubmitCallback,
                autoSendComplete,
                pagePath,
            ])

            const { field: controller } = useController({
                control,
                defaultValue: waitlistStore.contactInfo.phone,
                name: 'phone',
                rules: {
                    validate: (phone) =>
                        validateTelephoneInput(phone) || errorTexts.validate,
                    required: {
                        value: true,
                        message: errorTexts.required,
                    },
                },
            })

            const buttonLabel: string = (() => {
                if (!isExpanded) return title ?? ''
                if (isSubmitted) return TEXT.textMeAppLink.resend
                return TEXT.textMeAppLink.getStarted
            })()

            return (
                <form
                    onSubmit={onSubmit}
                    data-testid={testId}
                    data-expanded={isExpanded}
                    className={classNames(
                        styles.form,
                        styles[size],
                        styles[alignment],
                        styles[`placement--${placement}`],
                        displayOnLight ? styles.light : styles.dark,
                    )}
                >
                    <animated.div style={formStyles} className={styles.control}>
                        {isExpanded && (
                            <animated.div
                                style={inputStyles}
                                className={classNames(
                                    styles.field,
                                    styles[`field-${size}`],
                                )}
                            >
                                <Tooltip
                                    show={isFocused}
                                    content={
                                        <div>
                                            By providing your number, you
                                            confirm you are 13 or over and you
                                            consent to receive SMS marketing and
                                            transactional messages to the phone
                                            number provided. Msg & data rates
                                            may apply. See{' '}
                                            <Link
                                                href='/policies/mobileterms'
                                                target='_blank'
                                                onMouseDown={(e) =>
                                                    e.preventDefault()
                                                }
                                            >
                                                Mobile Terms
                                            </Link>{' '}
                                            for more information.
                                        </div>
                                    }
                                >
                                    <TelephoneInput
                                        {...controller}
                                        autoComplete='off'
                                        autoFocus={!forceExpanded}
                                        data-testid='getApp.field'
                                        size={size}
                                        disabled={isSubmitting}
                                        validationCount={submitCount}
                                        valid={!errors.phone}
                                        placeholder={placeholder}
                                        adornment={<CountryCode />}
                                        onFocus={() => {
                                            setIsFocused(true)
                                        }}
                                        onBlur={() => {
                                            // wait for link click propagation if link in tooltip was clicked
                                            setTimeout(() => {
                                                setIsFocused(false)
                                            }, 100)
                                        }}
                                        displayOnLight={displayOnLight}
                                    />
                                </Tooltip>
                            </animated.div>
                        )}
                        <animated.div
                            style={buttonStyles}
                            className={classNames(
                                styles.submit,
                                styles[`submit-${size}`],
                            )}
                        >
                            {autoSendComplete ? (
                                <div className={styles.autoSendSuccess}>
                                    {TEXT.textMeAppLink.success}
                                </div>
                            ) : isSubmitSuccessful ? (
                                <div className={styles.success}>
                                    {TEXT.textMeAppLink.sent}
                                </div>
                            ) : autoSendError ? (
                                // Fall back to normal form behavior on auto-send error
                                <Button
                                    {...buttonProps}
                                    data-testid='getApp.button'
                                    placement={placement}
                                    containerElementId={containerElementId}
                                    loading={
                                        (isExpanded && isSubmitting) || loading
                                    }
                                    disabled={isSubmitting}
                                    type={isExpanded ? 'submit' : 'button'}
                                    preset={
                                        preset ? (preset as Preset) : 'white'
                                    }
                                    size={size}
                                    ref={buttonRef}
                                    animatePress={isExpanded && isDirty}
                                    onClick={!isExpanded ? onExpand : undefined}
                                >
                                    {buttonLabel}
                                </Button>
                            ) : (
                                <Button
                                    {...buttonProps}
                                    data-testid='getApp.button'
                                    placement={placement}
                                    containerElementId={containerElementId}
                                    loading={
                                        (isExpanded && isSubmitting) || loading
                                    }
                                    disabled={isSubmitting}
                                    type={isExpanded ? 'submit' : 'button'}
                                    preset={
                                        preset ? (preset as Preset) : 'white'
                                    }
                                    size={size}
                                    ref={buttonRef}
                                    animatePress={isExpanded && isDirty}
                                    onClick={!isExpanded ? onExpand : undefined}
                                >
                                    {buttonLabel}
                                </Button>
                            )}
                        </animated.div>
                    </animated.div>

                    {isExpanded &&
                        showHelpText &&
                        (errors.phone ? (
                            <div
                                className={styles.error}
                                data-testid='getApp.error'
                            >
                                <ErrorMessage errors={errors} name='phone' />
                            </div>
                        ) : (
                            <div className={styles.why}>
                                {/*{TEXT.textMeAppLink.why}*/}
                            </div>
                        ))}
                </form>
            )
        },
    )

type Props = WithTestId<
    ButtonTrackingProps &
        Omit<ActionDownloadAppFragment, 'type' | 'sys'> & {
            buttonProps?: Partial<ButtonProps>
            forceExpanded?: boolean
            displayOnLight?: boolean
            inputColor?: string
            loading?: boolean
            onSubmitCallback?: () => void
            size?: 'small' | 'medium'
            alignment?: 'left' | 'center'
            phoneNumber?: string
            autoSend?: boolean
            /** Current page path for attribution tracking */
            pagePath?: string
            /** Collapse on scroll. Defaults to true for header placement or small size */
            collapseOnScroll?: boolean
        }
>
